// ====================================================================
// Agentic Hero — embedded chat client
// --------------------------------------------------------------------
// A second front-end for the SAME agent backend the drawer uses
// (assets/js/site/agent-mode.js). It speaks the identical SSE + REST
// protocol — streamUrl / tilesUrl / conversationUrl / leadUrl — so the
// agent itself is untouched. Config arrives via window.wlAgenticHeroConfig
// (printed by partials/components/agentic-hero.php).
//
// Differences vs. the drawer:
//   - Surfaced pages ('tiles' events) become the hero BACKGROUND — a
//     crossfading image set — instead of inline cards.
//   - A feature block (bottom-right) shows the title / excerpt / link for
//     the page currently displayed in the background, with navigation dots.
//   - CTA buttons are NOT rendered in the chat; the chat stays conversation
//     only. Stats and the lead form still render in the panel.
// ====================================================================

(function () {
  var cfg = window.wlAgenticHeroConfig;
  if (!cfg) return;

  var roots = document.querySelectorAll('[data-agentic-hero]');
  if (!roots.length) return;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  Array.prototype.forEach.call(roots, initHero);

  function initHero(root) {
    var log       = root.querySelector('[data-agentic-log]');
    var form      = root.querySelector('[data-agentic-form]');
    var input     = form ? form.querySelector('[data-agentic-input]') : null;
    var chips     = root.querySelectorAll('[data-agentic-chip]');
    var chipsWrap = root.querySelector('[data-agentic-chips]');
    var bgWrap    = root.querySelector('[data-agentic-bg]');
    // Feature block (bottom-right) + navigation dots for the surfaced pages.
    var featureEl = root.querySelector('[data-agentic-feature]');
    var dotsEl    = root.querySelector('[data-agentic-dots]');
    var titleEl   = root.querySelector('[data-agentic-feature-title]');
    var excerptEl = root.querySelector('[data-agentic-feature-excerpt]');
    var linkEl    = root.querySelector('[data-agentic-feature-link]');
    if (!log || !form || !input) return;

    // Editor-chosen pages to show in the background on load (before the agent
    // surfaces anything) — rendered through the same path as agent content.
    var startSlideIds = (root.getAttribute('data-agentic-slides') || '')
      .split(',')
      .map(function (s) { return parseInt(s, 10); })
      .filter(function (n) { return n > 0; });

    // Session lives in memory only: the hero starts a FRESH conversation on
    // every page load (unlike the drawer, which follows the visitor across
    // pages via sessionStorage). It still persists across turns within a
    // single page view.
    var sessionId = null;

    var abortCtl     = null;
    var busy         = false;
    var hasMessages  = false;
    var leadCaptured = false;

    // Background slider state.
    var slides     = [];   // DOM nodes
    var items      = [];    // tile data, parallel to slides
    var slideIndex = 0;
    var slideTimer = null;

    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ------------------------------------------------------------------
    // Text rendering
    // ------------------------------------------------------------------

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function renderMarkdown(raw) {
      var s = String(raw)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
      s = s.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
      s = s.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');

      var lines = s.split('\n');
      var out = [];
      var inList = false;
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        var m = line.match(/^\s*[-*]\s+(.*)$/);
        if (m) {
          if (!inList) { out.push('<ul>'); inList = true; }
          out.push('<li>' + m[1] + '</li>');
        } else {
          if (inList) { out.push('</ul>'); inList = false; }
          out.push(line);
        }
      }
      if (inList) out.push('</ul>');

      return out
        .join('\n')
        .replace(/<\/ul>\n/g, '</ul>')
        .replace(/\n<ul>/g, '<ul>')
        .replace(/\n/g, '<br>');
    }

    function scrollToEnd() { log.scrollTop = log.scrollHeight; }

    function addUserMsg(text) {
      var el = document.createElement('div');
      el.className = 'agentic-hero__msg agentic-hero__msg--user';
      el.textContent = text;
      log.appendChild(el);
      scrollToEnd();
      return el;
    }

    function addBotBubble() {
      var el = document.createElement('div');
      el.className = 'agentic-hero__msg agentic-hero__msg--bot';
      el.dataset.raw = '';
      log.appendChild(el);
      scrollToEnd();
      return el;
    }

    function addErrorMsg(text) {
      var el = document.createElement('div');
      el.className = 'agentic-hero__msg agentic-hero__msg--error';
      el.textContent = text;
      log.appendChild(el);
      scrollToEnd();
    }

    // ------------------------------------------------------------------
    // Activity indicator
    // ------------------------------------------------------------------

    function createActivity(label) {
      var el = document.createElement('div');
      el.className = 'agentic-hero__activity';
      el.innerHTML =
        '<span class="agentic-hero__dots-anim"><span></span><span></span><span></span></span>' +
        '<span class="agentic-hero__activity-label"></span>';
      el.querySelector('.agentic-hero__activity-label').textContent = label || 'Thinking…';
      log.appendChild(el);
      scrollToEnd();
      return el;
    }
    function setActivityLabel(el, text) {
      if (!el) return;
      var lbl = el.querySelector('.agentic-hero__activity-label');
      if (lbl) lbl.textContent = text;
    }
    function removeActivity(el) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }

    // ------------------------------------------------------------------
    // CTA buttons (render_ctas) — conversation shortcuts, in the panel.
    // These are NOT the tile link (that lives in the feature block); they
    // are the agent's "next step" buttons that send a message back in.
    // ------------------------------------------------------------------

    function renderCtas(ctas) {
      if (!ctas || !ctas.length) return;
      var wrap = document.createElement('div');
      wrap.className = 'agentic-hero__ctas';
      ctas.forEach(function (cta) {
        var el = buildCta(cta);
        if (el) wrap.appendChild(el);
      });
      if (wrap.children.length) {
        log.appendChild(wrap);
        scrollToEnd();
      }
    }

    function buildCta(cta) {
      if (!cta || !cta.label) return null;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'agentic-hero__cta';
      btn.textContent = cta.label;
      btn.addEventListener('click', function () {
        var msg = cta.message || cta.label;
        btn.disabled = true;
        sendMessage(msg, { fromCta: cta.label });
      });
      return btn;
    }

    // ------------------------------------------------------------------
    // Stat grid (in the panel)
    // ------------------------------------------------------------------

    function renderStats(stats) {
      if (!stats || !stats.length) return;
      var card = document.createElement('div');
      card.className = 'agentic-hero__stats';
      var grid = document.createElement('div');
      grid.className = 'agentic-hero__stats-grid';
      stats.forEach(function (s) {
        if (!s || !s.value) return;
        var box = document.createElement('div');
        box.className = 'agentic-hero__stat';
        var v = document.createElement('div');
        v.className = 'agentic-hero__stat-value';
        v.textContent = s.value;
        var l = document.createElement('div');
        l.className = 'agentic-hero__stat-label';
        l.textContent = s.label || '';
        box.appendChild(v);
        box.appendChild(l);
        grid.appendChild(box);
      });
      if (grid.children.length) {
        card.appendChild(grid);
        log.appendChild(card);
        scrollToEnd();
      }
    }

    // ------------------------------------------------------------------
    // Inline lead form (in the panel)
    // ------------------------------------------------------------------

    function renderLeadForm(opts) {
      opts = opts || {};
      if (leadCaptured) { renderLeadConfirmation(); return; }

      var interest = opts.interest || '';
      var lead = document.createElement('form');
      lead.className = 'agentic-hero__lead';
      lead.setAttribute('novalidate', 'novalidate');
      lead.innerHTML =
        '<div class="agentic-hero__lead-grid">' +
          '<label class="agentic-hero__lead-field"><span>Name</span>' +
            '<input type="text" name="name" autocomplete="name"></label>' +
          '<label class="agentic-hero__lead-field"><span>Email</span>' +
            '<input type="email" name="email" autocomplete="email" required></label>' +
          '<label class="agentic-hero__lead-field agentic-hero__lead-field--full"><span>Company</span>' +
            '<input type="text" name="company" autocomplete="organization"></label>' +
        '</div>' +
        '<div class="agentic-hero__lead-error" role="alert" hidden></div>' +
        '<div class="agentic-hero__lead-actions">' +
          '<button type="submit" class="agentic-hero__lead-submit">Send</button>' +
        '</div>';

      var errEl     = lead.querySelector('.agentic-hero__lead-error');
      var submitBtn = lead.querySelector('.agentic-hero__lead-submit');

      lead.addEventListener('submit', function (e) {
        e.preventDefault();
        var email   = lead.querySelector('[name="email"]').value.trim();
        var name    = lead.querySelector('[name="name"]').value.trim();
        var company = lead.querySelector('[name="company"]').value.trim();

        if (!EMAIL_RE.test(email)) {
          showLeadError(errEl, 'Please enter a valid email address.');
          return;
        }
        hideLeadError(errEl);
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending…';

        fetch(cfg.leadUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': cfg.nonce },
          body: JSON.stringify({
            session_id: sessionId || '',
            name: name, email: email, company: company, interest: interest,
          }),
        })
          .then(function (res) {
            return res.json().catch(function () { return {}; }).then(function (body) {
              return { status: res.status, body: body };
            });
          })
          .then(function (r) {
            if (r.status >= 200 && r.status < 300 && r.body && r.body.ok) {
              leadCaptured = true;
              swapToConfirmation(lead, r.body.message);
            } else {
              submitBtn.disabled = false;
              submitBtn.textContent = 'Send';
              showLeadError(errEl, (r.body && r.body.error) || 'Something went wrong — please try again.');
            }
          })
          .catch(function () {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Send';
            showLeadError(errEl, 'Network error — please try again.');
          });
      });

      log.appendChild(lead);
      scrollToEnd();
      var emailInput = lead.querySelector('[name="email"]');
      if (emailInput) emailInput.focus();
    }

    function showLeadError(el, msg) { if (el) { el.textContent = msg; el.hidden = false; } }
    function hideLeadError(el) { if (el) el.hidden = true; }
    function swapToConfirmation(formEl, msg) {
      var conf = buildLeadConfirmation(msg);
      if (formEl.parentNode) formEl.parentNode.replaceChild(conf, formEl);
      scrollToEnd();
    }
    function renderLeadConfirmation(msg) { log.appendChild(buildLeadConfirmation(msg)); scrollToEnd(); }
    function buildLeadConfirmation(msg) {
      var conf = document.createElement('div');
      conf.className = 'agentic-hero__lead-confirm';
      conf.textContent = msg || 'Thanks — your details are in. Someone from our team will reach out soon.';
      return conf;
    }

    // ------------------------------------------------------------------
    // Background — surfaced pages drive the hero imagery + feature block.
    // ------------------------------------------------------------------

    function updateBackground(ids) {
      if (!bgWrap || !ids || !ids.length) return;
      var url = cfg.tilesUrl + '?ids=' + encodeURIComponent(ids.join(','));
      fetch(url, { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : []; })
        .then(function (tiles) {
          var usable = (tiles || []).filter(function (t) { return t && t.thumbnail; });
          if (usable.length) renderBackground(usable);
        })
        .catch(function () { /* leave the current background in place */ });
    }

    // Crossfade-to-newest: build the new slide set on top, then drop the old
    // ones once the transition has run. The feature block + dots reflect the
    // freshly surfaced set.
    function renderBackground(tiles) {
      if (slideTimer) { clearInterval(slideTimer); slideTimer = null; }

      var oldSlides = bgWrap.querySelectorAll('.agentic-hero__bg-slide');
      items = tiles;
      slides = [];
      slideIndex = 0;

      var frag = document.createDocumentFragment();
      tiles.forEach(function (t) {
        var slide = document.createElement('div');
        slide.className = 'agentic-hero__bg-slide';
        // The image is always the poster (shows under/until the video plays,
        // and is the fallback when there's no video or motion is reduced).
        if (t.thumbnail) {
          slide.style.backgroundImage = "url('" + String(t.thumbnail).replace(/'/g, "\\'") + "')";
        }
        if (!reduceMotion && t.video && t.video.type) {
          slide.appendChild(buildSlideVideo(t.video));
        }
        frag.appendChild(slide);
        slides.push(slide);
      });
      bgWrap.appendChild(frag);

      buildDots();

      requestAnimationFrame(function () {
        showSlide(0);
        Array.prototype.forEach.call(oldSlides, function (o) { o.classList.remove('is-active'); });
        setTimeout(function () {
          Array.prototype.forEach.call(oldSlides, function (o) {
            if (o.parentNode) o.parentNode.removeChild(o);
          });
        }, 900);
      });

      startRotation();
    }

    function showSlide(i) {
      if (!slides.length || i < 0 || i >= slides.length) return;
      if (slides[slideIndex]) {
        slides[slideIndex].classList.remove('is-active');
        pauseSlideMedia(slides[slideIndex]);
      }
      slideIndex = i;
      slides[slideIndex].classList.add('is-active');
      playSlideMedia(slides[slideIndex]); // only the visible slide ever plays
      updateFeature();
      updateDots();
    }

    function startRotation() {
      if (slides.length < 2 || reduceMotion) return;
      slideTimer = setInterval(function () {
        showSlide((slideIndex + 1) % slides.length);
      }, 5000);
    }

    function buildDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      if (items.length < 2) { dotsEl.hidden = true; return; }
      items.forEach(function (t, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'agentic-hero__dot';
        dot.setAttribute('aria-label', 'Show ' + (t.title || ('item ' + (i + 1))));
        dot.addEventListener('click', function () {
          if (slideTimer) { clearInterval(slideTimer); slideTimer = null; }
          showSlide(i);
          startRotation();
        });
        dotsEl.appendChild(dot);
      });
      dotsEl.hidden = false;
    }

    function updateDots() {
      if (!dotsEl) return;
      var dots = dotsEl.querySelectorAll('.agentic-hero__dot');
      Array.prototype.forEach.call(dots, function (d, i) {
        d.classList.toggle('is-active', i === slideIndex);
        if (i === slideIndex) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }

    function updateFeature() {
      if (!featureEl) return;
      var t = items[slideIndex];
      if (!t) { featureEl.hidden = true; return; }
      if (titleEl)   titleEl.textContent   = t.title || '';
      if (excerptEl) excerptEl.textContent = t.excerpt || '';
      if (linkEl) {
        if (t.url) { linkEl.href = t.url; linkEl.hidden = false; }
        else { linkEl.hidden = true; }
      }
      featureEl.hidden = false;
    }

    // ------------------------------------------------------------------
    // Slide video — self-hosted <video> or YouTube/Vimeo iframe, mirroring
    // the case-study tile-media conventions. Only the active slide ever plays
    // (see showSlide); iframes lazy-load on first activation and then play/pause
    // over postMessage so they don't reload on every rotation.
    // ------------------------------------------------------------------

    function buildSlideVideo(video) {
      if (video.type === 'self') {
        var v = document.createElement('video');
        v.className = 'agentic-hero__bg-video';
        v.muted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        v.preload = 'metadata';
        v.src = video.src;
        return v;
      }
      var wrap = document.createElement('div');
      wrap.className = 'agentic-hero__bg-embed';
      var iframe = document.createElement('iframe');
      iframe.className = 'agentic-hero__bg-iframe';
      iframe.setAttribute('data-ah-video', video.type);
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
      iframe.setAttribute('tabindex', '-1');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.dataset.src = embedUrl(video); // loaded on first activation
      wrap.appendChild(iframe);
      return wrap;
    }

    function embedUrl(video) {
      if (video.type === 'youtube') {
        return 'https://www.youtube.com/embed/' + encodeURIComponent(video.id) +
          '?autoplay=1&mute=1&controls=0&loop=1&playlist=' + encodeURIComponent(video.id) +
          '&playsinline=1&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&enablejsapi=1';
      }
      if (video.type === 'vimeo') {
        return 'https://player.vimeo.com/video/' + encodeURIComponent(video.id) +
          '?autoplay=1&muted=1&loop=1&background=1&playsinline=1';
      }
      return '';
    }

    function playSlideMedia(slide) {
      var v = slide.querySelector('.agentic-hero__bg-video');
      if (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); return; }
      var f = slide.querySelector('.agentic-hero__bg-iframe');
      if (!f) return;
      if (f.dataset.src) {
        // First activation — lazy-load the embed (autoplays via URL params).
        f.addEventListener('load', function () {
          if (f.getAttribute('data-ah-video') === 'youtube' && f.contentWindow) {
            f.contentWindow.postMessage('{"event":"listening"}', '*'); // YT command handshake
          }
        }, { once: true });
        f.src = f.dataset.src;
        delete f.dataset.src;
      } else {
        iframeCmd(f, 'play');
      }
    }

    function pauseSlideMedia(slide) {
      var v = slide.querySelector('.agentic-hero__bg-video');
      if (v) { try { v.pause(); } catch (e) {} return; }
      var f = slide.querySelector('.agentic-hero__bg-iframe');
      if (f && !f.dataset.src) iframeCmd(f, 'pause'); // only if already loaded
    }

    function iframeCmd(f, action) {
      var win = f.contentWindow;
      if (!win) return;
      var type = f.getAttribute('data-ah-video');
      if (type === 'youtube') {
        win.postMessage(JSON.stringify({
          event: 'command',
          func: action === 'play' ? 'playVideo' : 'pauseVideo',
          args: []
        }), '*');
      } else if (type === 'vimeo') {
        win.postMessage(JSON.stringify({ method: action === 'play' ? 'play' : 'pause' }), '*');
      }
    }

    // ------------------------------------------------------------------
    // SSE parsing + per-turn event handling
    // ------------------------------------------------------------------

    function makeSseParser(onEvent) {
      var buf = '';
      var decoder = new TextDecoder('utf-8');
      return function (chunk) {
        buf += decoder.decode(chunk, { stream: true });
        var idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          var raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          var dataLines = raw.split('\n')
            .filter(function (l) { return l.indexOf('data:') === 0; })
            .map(function (l) { return l.slice(5).replace(/^ /, ''); });
          if (!dataLines.length) continue;
          try { onEvent(JSON.parse(dataLines.join('\n'))); } catch (e) { /* skip malformed */ }
        }
      };
    }

    function makeEventHandler(message) {
      addUserMsg(message);

      var textBuffer = '';
      var textBubble = null;

      // One bubble per text run — see agent-mode.js for rationale.
      function appendText(delta) {
        textBuffer += (delta || '');
        if (!textBubble) textBubble = addBotBubble();
        textBubble.dataset.raw = textBuffer;
        textBubble.innerHTML   = renderMarkdown(textBuffer);
        scrollToEnd();
      }
      function resetTextRun() { textBuffer = ''; textBubble = null; }

      // Tiles are buffered and rendered only when the turn ENDS. Loading the
      // (large) background images mid-stream competes with the live SSE
      // connection and can cut the stream off before the follow-up arrives —
      // which showed up as a follow-up that never came + a spinner that hung.
      var pendingTiles = null;

      var activity = createActivity('Thinking…');
      var labelTimers = [];

      // The indicator's label is driven by 'phase' events from the backend
      // (AnthropicClient::stream_turn yields phases at iteration starts and
      // before each content tool). Each phase label describes what's being
      // PREPARED — the indicator is hidden the moment any content actually
      // streams. The cold-start fallbacks below cover the brief network gap
      // before the first phase event arrives.
      var phaseLabels = {
        initial:  'Thinking…',
        answer:   'Preparing initial response…',
        content:  'Finding useful content…',
        followup: 'Compiling follow-up…',
        lead:     'Loading contact form…'
      };
      var currentPhase = 'initial';

      labelTimers.push(setTimeout(function () {
        if (currentPhase === 'initial' && activity) setActivityLabel(activity, 'Looking through our work…');
      }, 3500));
      labelTimers.push(setTimeout(function () {
        if (currentPhase === 'initial' && activity) setActivityLabel(activity, 'Putting together a response…');
      }, 8000));

      function clearColdTimers() {
        labelTimers.forEach(clearTimeout);
        labelTimers = [];
      }

      function setActivityVisible(visible) {
        if (!activity) return;
        activity.classList.toggle('is-hidden', !visible);
      }

      function setPhase(phase) {
        if (!activity) return;
        currentPhase = phase;
        setActivityLabel(activity, phaseLabels[phase] || 'Working…');
        setActivityVisible(true);
      }

      function setStatusLabel(text) {
        if (!activity) return;
        setActivityLabel(activity, text || '…');
        setActivityVisible(true);
      }

      // Runs when the turn ends — on a 'done'/'error' event OR when the stream
      // simply closes without one (dropped connection / timeout), which used to
      // leave the loader spinning forever. Clears the indicator and renders the
      // deferred background. Idempotent.
      function finalize() {
        clearColdTimers();
        if (activity) { removeActivity(activity); activity = null; }
        if (pendingTiles && pendingTiles.length) updateBackground(pendingTiles);
        pendingTiles = null;
      }

      var handler = function (ev) {
        switch (ev.type) {
          case 'session':
            sessionId = ev.id;
            break;
          case 'phase':
            // The backend tells us what's being prepared next — show the
            // matching label. See AnthropicClient::stream_turn.
            clearColdTimers();
            setPhase(ev.phase);
            break;
          case 'status':
            // Tool-specific descriptive label, e.g. "Searching for X…".
            clearColdTimers();
            setStatusLabel(ev.message);
            break;
          case 'text':
            // Content is arriving — hide the indicator while it streams.
            clearColdTimers();
            setActivityVisible(false);
            appendText(ev.delta);
            break;
          case 'tiles':
            // Surfaced pages drive the background + feature block, not the chat.
            // Buffer the ids and render at finalize() (turn end) so the image
            // loads don't compete with the live SSE stream — see pendingTiles.
            clearColdTimers();
            setActivityVisible(false);
            resetTextRun();
            pendingTiles = ev.ids || [];
            break;
          case 'ctas':
            clearColdTimers();
            setActivityVisible(false);
            resetTextRun();
            renderCtas(ev.items || []);
            break;
          case 'stats':
            clearColdTimers();
            setActivityVisible(false);
            resetTextRun();
            renderStats(ev.items || []);
            break;
          case 'lead_form':
            clearColdTimers();
            setActivityVisible(false);
            resetTextRun();
            renderLeadForm({ interest: ev.interest || '' });
            break;
          case 'done':
            finalize();
            break;
          case 'error':
            finalize();
            addErrorMsg(ev.message || 'Something went wrong.');
            break;
        }
      };

      handler.finalize = finalize;
      return handler;
    }

    // ------------------------------------------------------------------
    // Submit flow
    // ------------------------------------------------------------------

    function setBusy(state) {
      busy = !!state;
      root.classList.toggle('agentic-hero--busy', busy);
      input.disabled = busy;
      var send = form.querySelector('.agentic-hero__send');
      if (send) send.disabled = busy;
    }

    function markConversing() {
      if (hasMessages) return;
      hasMessages = true;
      // Replaces the intro copy + chips with the conversation (see SCSS).
      root.classList.add('agentic-hero--conversing');
      if (chipsWrap) chipsWrap.hidden = true;
    }

    function sendMessage(message, opts) {
      if (!message || busy) return;
      opts = opts || {};
      markConversing();
      setBusy(true);

      var handle = makeEventHandler(message);
      var parse  = makeSseParser(handle);
      abortCtl = new AbortController();

      var payload = { message: message, referrer_url: window.location.href };
      if (sessionId) payload.session_id = sessionId;
      if (opts.fromCta) payload.from_cta = opts.fromCta;

      fetch(cfg.streamUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'X-WP-Nonce': cfg.nonce,
        },
        body: JSON.stringify(payload),
        signal: abortCtl.signal,
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (body) {
              handle({ type: 'error', message: body.error || body.message || ('Error: ' + res.status) });
            });
          }
          var reader = res.body.getReader();
          function pump() {
            return reader.read().then(function (r) {
              if (r.done) return;
              parse(r.value);
              return pump();
            });
          }
          return pump();
        })
        .catch(function (e) {
          if (e && e.name === 'AbortError') return;
          handle({ type: 'error', message: 'Network error: ' + (e && e.message || 'unknown') });
        })
        .then(function () {
          abortCtl = null;
          // Stream fully settled. Finalize even if no 'done' event arrived
          // (dropped connection / timeout) so the loader never hangs and the
          // deferred background still renders. Idempotent with the 'done' case.
          if (handle && handle.finalize) handle.finalize();
          setBusy(false);
          requestAnimationFrame(function () { input.focus(); });
        });
    }

    // ------------------------------------------------------------------
    // Wire up
    // ------------------------------------------------------------------

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var msg = input.value.trim();
      if (!msg) return;
      input.value = '';
      sendMessage(msg);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        form.requestSubmit ? form.requestSubmit() : sendMessage(input.value.trim());
      }
    });

    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        var text = chip.dataset.prompt || chip.textContent.trim();
        if (text) sendMessage(text);
      });
    });

    // Paint the editor-chosen starting slides on load — same render path as
    // agent-pulled content (crossfades over the initial inline image).
    if (startSlideIds.length) updateBackground(startSlideIds);
  }
})();
